import type { LucideIcon } from 'lucide-react';
import type {
  CanvasEntity,
  Rect,
  ConnectorPosition,
} from '../entityTypes';
import { getConnectorData, getGroupData } from '../entityTypes';

// ============================================
// SplitEntities: used by onRemove to operate on
// nodes, connectors, and groups independently
// ============================================

export interface SplitEntities {
  nodes: Record<string, CanvasEntity>;
  connectors: Record<string, CanvasEntity>;
  groups: Record<string, CanvasEntity>;
}
import type { ContextMenuItem } from '../../components/ContextMenu';
import type { CanvasStore } from '../stores/canvasStore';
import type { CanvasUiStore } from '../stores/canvasUiStore';
import type { CanvasLayer, CanvasGroup, CanvasSegment } from '../types';

// ============================================
// Context object passed to getContextMenuItems
// ============================================

export interface EntityMenuContext {
  store: CanvasStore;
  entity: CanvasEntity;
  layers: CanvasLayer[];
  groups: CanvasGroup[];
  segments: CanvasSegment[];
  selectedEntityIds: string[];
  enteredGroupId: string | null;
  onRemoveSelected?: () => void;
  onCreateGroup?: () => void;
  onGroupToggleCollapse?: (groupId: string) => void;
  setGroupPickerState?: (state: { targetNodeIds: string[] } | null) => void;
  onActivateDepExplorer?: (entityId: string) => void;
}

// ============================================
// Toolbar Actions
// ============================================

export interface ToolbarActionOption {
  id: string;
  label: string;
  color?: string;    // for color swatches (hex/rgba)
}

export interface ToolbarAction {
  name: string;           // merge key, e.g. 'backgroundColor', 'textColor'
  label: string;          // display name, e.g. 'Hintergrund'
  type: 'color' | 'select' | 'toggle';
  options?: ToolbarActionOption[];  // for color/select
  getValue: () => unknown;
  apply: (value: unknown) => void;
  allowCustomInput?: boolean;    // Zeigt Freitext-Eingabefeld im Select-Popover
  customInputUnit?: string;      // Einheit (z.B. 'px')
  customInputMin?: number;
  customInputMax?: number;
  customInputStep?: number;
}

export interface ToolbarActionContext {
  store: CanvasStore;
  entity: CanvasEntity;
}

// ============================================
// Props interface for entity node components
// ============================================

export interface EntityNodeProps {
  entity: CanvasEntity;
}

// ============================================
// Add Picker Entry
// ============================================

export interface AddPickerEntry {
  label: string;
  icon: LucideIcon;
  iconColor: string;
  priority: number; // lower = shown first
  /** 'taskPicker' = opens task search subview; 'instant' = creates entity immediately */
  mode: 'instant' | 'taskPicker';
  /** Optional sub-items shown as a nested submenu instead of a direct click */
  children?: Array<{ id: string; label: string; icon?: LucideIcon }>;
}

// ============================================
// Event delegation context
// ============================================

export interface EntityInteractionContext {
  entity: CanvasEntity;
  store: CanvasStore;
  uiStore: CanvasUiStore;
  redirectGroupId: string | null;
  isEditing: boolean;
  isLocked: boolean;
  handleEntityDragStart: (entityId: string, e: React.PointerEvent) => void;
  handleGroupDragStart: (groupId: string, e: React.PointerEvent) => void;
  handleWaypointDragStart: (waypointId: string, e: React.PointerEvent) => void;
  handleWaypointConnectorMouseDown: (waypointId: string, e: React.PointerEvent) => void;
  handleEntityContextMenu: (entityId: string, e: React.MouseEvent) => void;
}

export interface PointerDownGuardResult {
  handled: boolean;
}

export interface EntityClickResult {
  afterSelect?: () => void;
}

export interface DoubleClickResult {
  handled: boolean;
}

// ============================================
// Abstract base class for entity kind handlers
// ============================================

export abstract class EntityKindHandler {
  abstract readonly kind: string;

  // React component for canvas rendering (null = not individually rendered, e.g. connector)
  readonly Component: React.ComponentType<EntityNodeProps> | null = null;

  // --- DOM ---
  /** data attribute name for finding the DOM element, e.g. 'data-canvas-node' */
  readonly dataAttribute: string | null = null;

  /** Compute CSS left/top from entity coordinates. Override for offset kinds (e.g. waypoint). */
  getDomPosition(entity: CanvasEntity): { left: number; top: number } {
    return { left: entity.x, top: entity.y };
  }

  // --- Capabilities ---
  canDrag = true;
  canSnap = true;
  canBoxSelect = true;
  canConnect = false;
  canResize = false;
  canInlineEdit = false;
  autoMeasure = false;
  /** Entity has its own x/y/width/height rect (false for connector/group which have derived or no bounds) */
  hasOwnRect = true;

  // --- Bounds ---
  // Default: entity.x/y/w/h. Override for derived bounds (expanded groups, connectors).
  getBounds(entity: CanvasEntity, _allEntities?: CanvasEntity[]): Rect {
    return { x: entity.x, y: entity.y, width: entity.width, height: entity.height };
  }

  // --- Connector Positions ---
  getConnectorPositions(_entity: CanvasEntity): ConnectorPosition[] {
    return [];
  }

  // --- Reducer Hooks ---
  // Called inside generic entity.remove to handle cleanup (connector removal, group membership, etc.)
  onRemove(entityId: string, split: SplitEntities): SplitEntities {
    // Default: remove the entity from nodes, remove connectors pointing to it, and clean group membership
    const { [entityId]: _, ...remainingNodes } = split.nodes;
    const remainingConnectors: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(split.connectors)) {
      const d = getConnectorData(e);
      if (d.from.entityId !== entityId && d.to.entityId !== entityId) {
        remainingConnectors[id] = e;
      }
    }
    const remainingGroups: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(split.groups)) {
      const gd = getGroupData(e);
      if (gd.memberEntityIds.includes(entityId)) {
        remainingGroups[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter((mid: string) => mid !== entityId) } };
      } else {
        remainingGroups[id] = e;
      }
    }
    return {
      nodes: remainingNodes,
      connectors: remainingConnectors,
      groups: remainingGroups,
    };
  }

  // --- Add Picker ---
  // Return non-null to appear in the "Hinzufügen" picker modal.
  getAddPickerEntry(): AddPickerEntry | null {
    return null;
  }

  // --- Toolbar Actions ---
  getToolbarActions(_ctx: ToolbarActionContext): ToolbarAction[] {
    return [];
  }

  // --- Context Menu ---
  getContextMenuItems(_ctx: EntityMenuContext): ContextMenuItem[] {
    return [];
  }

  // --- Event Delegation ---
  /** Return { handled: true } to skip common drag-or-select logic. */
  onPointerDownGuard(_ctx: EntityInteractionContext, _e: React.PointerEvent): PointerDownGuardResult | null {
    return null;
  }

  /** Called on click (no drag). Return { afterSelect } to run post-select behavior. */
  onEntityClick(_ctx: EntityInteractionContext, _shiftKey: boolean): EntityClickResult | null {
    return null;
  }

  /** Return { handled: true } to override default group-enter double-click. */
  onDoubleClick(_ctx: EntityInteractionContext, _e: React.MouseEvent): DoubleClickResult | null {
    return null;
  }

  // Called when entity's layer changes — removes entity from groups on a different layer
  onLayerChange(entity: CanvasEntity, newLayerId: string, groups: Record<string, CanvasEntity>): Record<string, CanvasEntity> {
    const result: Record<string, CanvasEntity> = {};
    for (const [id, e] of Object.entries(groups)) {
      const gd = getGroupData(e);
      if (gd.memberEntityIds.includes(entity.id) && e.layerId !== newLayerId) {
        result[id] = { ...e, data: { ...e.data, memberEntityIds: gd.memberEntityIds.filter((mid: string) => mid !== entity.id) } };
      } else {
        result[id] = e;
      }
    }
    return result;
  }
}
